/**
 * The one place a bearer token is attached and the one place `fetch` is called.
 *
 * `ApiError` lives here rather than in `client.ts` so every endpoint module can
 * import the transport without importing the whole `api` object — and, more
 * importantly, so there is exactly ONE `ApiError` class. Two classes with the
 * same name make `error instanceof ApiError` true or false depending on which
 * module threw, and every screen that switches on `error.code` would silently
 * stop switching.
 *
 * Components never call this directly. They call the typed helpers in `api/*`.
 */
import { accessToken } from '../auth/supabase';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The whole error body — a 409 carries ids the UI needs to offer a way out. */
    readonly payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();

  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        // ONLY when there is a body. Fastify rejects a request that declares
        // a JSON content-type and then sends nothing with
        // "Body cannot be empty when content-type is set" — so setting this
        // unconditionally made every bodyless DELETE in the app fail with a
        // 400 that read like a broken feature.
        ...(init.body !== undefined && init.body !== null ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    // fetch only rejects on a network failure, never on a 4xx/5xx.
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (!res.ok) {
    // The API always returns {error, message}; fall back if something else did.
    let body: { error?: string; message?: string } = {};
    try {
      body = (await res.json()) as { error?: string; message?: string };
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      body.error ?? 'internal_error',
      body.message ?? `Request failed (${res.status}).`,
      body,
    );
  }

  // 204 has no body to parse.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
